import { Collection } from 'firestorter';
import firebase from 'firebase/app';
import { firestorePaths } from '../utils/paths';
import Collections from '../utils/collections';
import BaseDocument from './BaseDocument';

class CheckInTheme extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.CHECK_IN_THEMES}/${id}`, opts);
  }

  get startDate() {
    return this.data.startDate;
  }

  get endDate() {
    return this.data.endDate;
  }

  get title() {
    return this.data.title;
  }

  get description() {
    return this.data.description;
  }

  get isActive() {
    return this.data.isActive;
  }

  get isArchived() {
    return !!this.data.isArchived;
  }

  updateTheme(startDate, endDate, title, description) {
    const updateData = {
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      title,
      description,
    };
    return this.updateFields(updateData);
  }

  archive(archivedBy) {
    const updateData = {
      isArchived: true,
      archivedAt: new Date(),
      archivedBy,
    };
    return this.updateFields(updateData);
  }

  static async getActiveTheme() {
    const queryResult = new Collection(Collections.CHECK_IN_THEMES, {
      createDocument: (src, opts) => new CheckInTheme(src.id, opts),
      query: (ref) => ref
        .where('isActive', '==', true)
        .limit(1),
    });
    await queryResult.fetch();
    return queryResult.docs[0] || null;
  }

  static async getThemeForCurrentDate() {
    const currentDate = new Date();

    /*
      We cannot filter documents using inequality operators in multiple fields in Firestore. So we fetch the ones
      that have an endDate greater than or equal to the current date and then we'll filter in memory to find the
      theme where currentDate is between startDate and endDate.
    */
    const checkInThemeCollection = new Collection(firestorePaths.CHECK_IN_THEMES, {
      createDocument: (src, opts) => new CheckInTheme(src.id, opts),
      query: (ref) => ref
        .where('endDate', '>=', currentDate)
        .orderBy('endDate', 'desc'),
    });

    await checkInThemeCollection.fetch();

    /*
      Filter in memory to find the theme where currentDate is between startDate and endDate. We already have the themes
      that end after the current date, so we only need to filter the ones that start before the current date.
    */
    const matchingTheme = checkInThemeCollection.docs.find((theme) => (
      !theme.isArchived && theme.startDate.toDate() <= currentDate
    ));

    return matchingTheme || null;
  }

  /**
   * Creates a new check-in theme in the Firestore 'checkInThemes' collection.
   * Sets the new theme as active and deactivates all other existing themes.
   *
   * @param {Date} startDate - The start date associated with the theme.
   * @param {Date} endDate - The end date associated with the theme.
   * @param {string} title - The title of the check-in theme.
   * @param {string} description - A detailed description of the theme.
   * @returns {Promise<string>} The ID of the newly created document.
   */
  static async create(startDate, endDate, title, description) {
    const checkInThemeCollection = new Collection(firestorePaths.CHECK_IN_THEMES);

    // Deactivate active themes first
    await CheckInTheme.deactivateActiveTheme();

    // Create the new theme and set it as active
    const theme = await checkInThemeCollection.add({
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      title,
      description,
      isActive: true,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    return theme.id;
  }

  /**
   * Deactivates active theme by setting its 'isActive' field to false.
   * Note: There is only one active theme at a time.
   * @returns {Promise<void>}
   */
  static async deactivateActiveTheme() {
    const activeTheme = await this.getActiveTheme();
    await activeTheme.update({ isActive: false });
  }

  /**
   * Retrieves all check-in themes from Firestore, ordered by their creation date (descending).
   * @returns {Promise<Collection>} A Firestorter Collection object containing CheckInTheme documents.
   */
  static async getAllThemes() {
    const checkInThemeCollection = new Collection(firestorePaths.CHECK_IN_THEMES, {
      createDocument: (source, options) => new CheckInTheme(source.id, options),
      query: (query) => query.orderBy('createdAt', 'desc'),
    });
    await CheckInTheme.initCollection(checkInThemeCollection);
    return checkInThemeCollection;
  }
}

export default CheckInTheme;
